import {
  type CareOrder,
  copyText,
  IconName,
  ModeEnum,
  PricingModeEnum,
  type Quote,
  SideEnum,
  toBig,
} from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { getStFQuotePriceCopyText } from '../../../../components/OMS/NewRFQ/Components/Quotes/StFCopyQuotePriceButton';
import { calculateBestQuotes, type RfqBestQuoteBy } from '../../../../utils/rfq';

/**
 * Looks at all the nodes the user has selected, filters down to the Quote rows, and maps them to the text line to copy
 * Returns a menu item if it found any relevant quote rows for copying the best quote price, otherwise returns undefined
 */
export function getCopyFundQuotePricesItem(
  quotesWithParentCOs: { quote: Quote | undefined; parentCO: CareOrder | undefined }[],
  bestBy?: RfqBestQuoteBy
): MenuItemDef | undefined {
  const textLinesToCopyByPrice = getTextLinesToCopy(quotesWithParentCOs, 'px');
  if (textLinesToCopyByPrice.length === 0) {
    return undefined;
  }

  const hasSizeCounteringQuote = quotesWithParentCOs.some(
    ({ quote }) => quote?.NegotiationParameters?.SizeCountering === ModeEnum.Enabled
  );
  const createAction = (copyBestBy: RfqBestQuoteBy) => {
    const combinedText = getTextLinesToCopy(quotesWithParentCOs, copyBestBy).join(', \n');
    return () => copyText(combinedText);
  };

  const item: MenuItemDef = {
    name: `Copy best quote (${textLinesToCopyByPrice.length})`,
    icon: `<i class="ag-icon ${IconName.ClipboardCopy}"/>`,
  };

  if (bestBy == null && hasSizeCounteringQuote) {
    item.subMenu = [
      {
        name: 'By Price',
        action: createAction('px'),
      },
      {
        name: 'By Quantity',
        action: createAction('qty'),
      },
    ];
    return item;
  }

  return {
    ...item,
    action: createAction(bestBy ?? 'px'),
  };
}

function getTextLinesToCopy(
  quotesWithParentCOs: { quote: Quote | undefined; parentCO: CareOrder | undefined }[],
  bestBy: RfqBestQuoteBy
): string[] {
  return compact(
    quotesWithParentCOs.map(({ quote, parentCO }) => quoteToCopyBestFundQuoteText(quote, parentCO, bestBy))
  );
}

/** Resolves the best quote price text from a single quote row node. Returns undefined otherwise */
function quoteToCopyBestFundQuoteText(
  quote: Quote | undefined,
  parentCO: CareOrder | undefined,
  bestBy?: RfqBestQuoteBy
): string | undefined {
  if (!quote || !parentCO) {
    return undefined;
  }

  if (quote.PricingMode !== PricingModeEnum.SpreadToFixing) {
    return undefined;
  }

  const quoteMarkets = calculateBestQuotes({ quotes: quote.Markets, pricingMode: quote.PricingMode, bestBy });

  const fund = parentCO.fund;
  const unitQty = parentCO.units;
  // Even if the RFQ can be 2-way, its executing side is defined by the CO which will always be either Buy or Sell
  const side = parentCO.Side;
  const settleOffset = parentCO.Annotations?.SettleOffset;

  // Given the side (telling us if we should look at Bid or Offer), find the QM designated as best bid or best offer
  const bestQuoteMarket = quoteMarkets.find(qm => (side === SideEnum.Buy ? qm.isBestOffer : qm.isBestBid));
  const px = side === SideEnum.Buy ? bestQuoteMarket?.OfferPx : bestQuoteMarket?.BidPx;
  const scaledPx = toBig(px)?.times(10_000).toFixed(); // Because we're StF

  const textToCopy = getStFQuotePriceCopyText({ fund, unitQty, side, px: scaledPx, settleOffset });
  if (!textToCopy) {
    // We weren't able to calculate the text to copy, meaning that its not supported for whatever case we are in
    return undefined;
  }

  return textToCopy;
}
